import React, { useEffect, useState, useCallback, Dispatch } from 'react';
import { Dropdown } from 'components/reusable/Dropdown';
import { Coordinate } from 'types/map';
import { MarkerType } from 'types/marker';
import { objectIsEqual } from 'utils/helpers';
import {
  getMarkers,
  changeMarker,
  addMarker,
  deleteMarker,
} from 'data/mapData';
import { EditMarkers } from './EditMarkers';
import { useAppDispatch } from 'hooks/hooks';
import { add, remove, replace, change } from 'hooks/markersSlice';

const EDIT_ID = 'NEW';

type Props = {
  styles: any;
  displayError: Dispatch<any>;
  confirmAction: Dispatch<any>;
  coords?: Coordinate[];
};

export function Markers({ styles, displayError, confirmAction }: Props) {
  const dispatch = useAppDispatch();
  const [originalMarkers, setOriginalMarkers] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const updateMarkers = useCallback(async () => {
    console.log('----------- GETTING MARKERS DATA -----------');
    getMarkers().then((data) => {
      setOriginalMarkers(data);
      dispatch(
        replace({
          markers: data,
        }),
      );
    });
  }, [dispatch]);

  const addNewMarker = useCallback(() => {
    const newMarker: MarkerType = {
      id: EDIT_ID,
      title: '',
      latlng: undefined,
    };

    dispatch(
      add({
        marker: newMarker,
      }),
    );
  }, [dispatch]);

  const cancelChanges = useCallback(
    (marker: MarkerType) => {
      if (marker.id === EDIT_ID) {
        dispatch(remove({ id: marker.id }));
      } else {
        const original = originalMarkers.find(
          (m: MarkerType) => m.id === marker.id,
        );
        dispatch(
          change({
            marker: original,
          }),
        );
      }
    },
    [originalMarkers, dispatch],
  );

  const saveMarker = useCallback(
    (marker: MarkerType) => {
      if (marker.id === EDIT_ID) {
        return addMarker(marker).then(updateMarkers);
      }

      return changeMarker(marker).then(updateMarkers);
    },
    [updateMarkers],
  );

  const hasChanged = useCallback(
    (marker: MarkerType) => {
      if (originalMarkers) {
        const original = originalMarkers.find(
          (o: MarkerType) => o.id === marker.id,
        );
        return !objectIsEqual(original, marker);
      }
    },
    [originalMarkers],
  );

  const actualDeleteMarker = useCallback(
    (id: string) => {
      if (id === EDIT_ID) {
        dispatch(
          remove({
            id,
          }),
        );

        return Promise.resolve();
      }

      return deleteMarker(id)
        .then(updateMarkers)
        .catch(() =>
          displayError('An error has occurred please try again later.'),
        );
    },
    [updateMarkers, displayError, dispatch],
  );

  const confirmDelete = useCallback(
    (marker: MarkerType) => {
      confirmAction({
        title: 'Delete Marker',
        name: marker.title,
        delete: () => actualDeleteMarker(marker.id),
      });
    },
    [confirmAction, actualDeleteMarker],
  );

  useEffect(() => {
    updateMarkers();
  }, [updateMarkers]);

  return (
    <Dropdown
      title={'Markers'}
      isCollapsed={isCollapsed}
      setCollapsed={() => setIsCollapsed(!isCollapsed)}
    >
      <EditMarkers
        styles={styles}
        hasChanged={hasChanged}
        addMarker={addNewMarker}
        deleteMarker={confirmDelete}
        saveMarker={saveMarker}
        cancelChanges={cancelChanges}
        displayError={displayError}
      />
    </Dropdown>
  );
}
